import { useQuery } from "@tanstack/react-query";
import { adminApi } from "../../api/admin";
import { ApiError } from "../../api/client";
import type { StaffMe, StaffProduct } from "../../api/types";
import { useMe } from "../../shared/hooks/useMe";

/** Where the chosen product is remembered between visits (docs/14 §1). */
export const PRODUCT_KEY = "admin.product";

export function rememberProduct(slug: string): void {
  try {
    localStorage.setItem(PRODUCT_KEY, slug);
  } catch {
    // A browser without storage still works; the picker just forgets the choice.
  }
}

export function rememberedProduct(): string | null {
  try {
    return localStorage.getItem(PRODUCT_KEY);
  } catch {
    return null;
  }
}

/** The product to open when none is in the URL: the remembered one, else the first allowed. */
export function defaultProduct(products: StaffProduct[]): StaffProduct | null {
  if (products.length === 0) return null;
  const remembered = rememberedProduct();
  return products.find((p) => p.slug === remembered) ?? products[0];
}

/** Which sections a product shows, from its capabilities (docs/14 §2). */
export function sectionsOf(product: StaffProduct): [string, string][] {
  const has = (name: string) => product.capabilities.includes(name);
  const base = `/admin/p/${product.slug}`;
  const links: [string, string][] = [
    [base, "Overview"],
    [`${base}/users`, "Users"],
  ];
  if (has("billing")) {
    links.push(
      [`${base}/payments`, "Payments"],
      [`${base}/ledger`, "Ledger"],
      [`${base}/promo-codes`, "Promo codes"],
    );
  }
  if (has("devices")) links.push([`${base}/devices`, "Devices"]);
  links.push([`${base}/events`, "Events"], [`${base}/audit`, "Audit"]);
  return links;
}

/** The signed-in staff member's platform role and the products they may open. */
export function useAdminWorkspace() {
  const me = useMe();
  const query = useQuery<StaffMe>({
    queryKey: ["admin", "me", me.data?.id ?? null],
    enabled: !!me.data,
    queryFn: async () => {
      try {
        return await adminApi.me();
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 403 || err.status === 401)
        ) {
          return { platform_role: null, products: [] };
        }
        throw err;
      }
    },
    staleTime: 60_000,
  });
  const data = query.data ?? { platform_role: null, products: [] };
  return {
    platformRole: data.platform_role,
    products: data.products,
    isStaff: data.platform_role !== null || data.products.length > 0,
    isLoading: me.isLoading || query.isLoading,
  };
}
